import { rem } from 'polished'
import React, { type ChangeEvent } from 'react'
import { DATA_TEST_CLASS } from 'shared-constants/build/testIds'
import styled from 'styled-components'
import {
  BORDER_WIDTH,
  COLOR_INTENT,
  FONT_SIZE,
  FONT_WEIGHT,
  RADIUS,
  SPACE,
} from 'Theme'

export const TOGGLE_BUTTON_HEIGHT = {
  PX_36: rem(36),
} as const
type ToggleButtonHeightType =
  (typeof TOGGLE_BUTTON_HEIGHT)[keyof typeof TOGGLE_BUTTON_HEIGHT]

export interface ToggleButtonProps {
  isUnavailable?: boolean
  onChange?: (
    event: ChangeEvent<HTMLInputElement>,
    isUnavailable?: boolean
  ) => void
  value: string
  groupValue?: string
  name?: string
  height?: ToggleButtonHeightType
  children: string
}

const ToggleButtonBase = styled('label')`
  input {
    display: none;
  }
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  font-size: ${FONT_SIZE.PX_13};
  color: ${COLOR_INTENT.TOGGLE_BUTTON.TEXT};
  font-weight: ${FONT_WEIGHT.MEDIUM};
  padding: 0 ${SPACE.PX_16};
  border-radius: ${RADIUS.PX_6};
  background: ${({ backgroundColor }) => backgroundColor};
  ${({ borderColor }) => `
    border: ${BORDER_WIDTH.PX_1} solid ${borderColor};
  `}
  ${({ isUnavailable, isChecked }) =>
    isUnavailable &&
    !isChecked &&
    `
    color: ${COLOR_INTENT.TOGGLE_BUTTON.TEXT_DISABLED}; 
    `}
  }
  ${({ height }) =>
    height &&
    `
    height: ${height};
  `}
  transition: border cubic-bezier(0.4, 0, 0.2, 1) 300ms, background cubic-bezier(0.4, 0, 0.2, 1) 300ms;
  &:hover {
    border-color: ${COLOR_INTENT.TOGGLE_BUTTON.BORDER_COLOR_HOVER};
    color: ${COLOR_INTENT.TOGGLE_BUTTON.TEXT};
  }
`

const getBackgroundColor = (isUnavailable: boolean, isChecked: boolean) => {
  if (isChecked) {
    return COLOR_INTENT.TOGGLE_BUTTON.BACKGROUND_CHECKED
  }
  if (isUnavailable) {
    return COLOR_INTENT.TOGGLE_BUTTON.BACKGROUND_DISABLED
  }
  return COLOR_INTENT.TOGGLE_BUTTON.BACKGROUND
}

const getBorderdColor = (isUnavailable: boolean, isChecked: boolean) => {
  if (isChecked) {
    return COLOR_INTENT.TOGGLE_BUTTON.BORDER_COLOR_CHECKED
  }
  if (isUnavailable) {
    return COLOR_INTENT.TOGGLE_BUTTON.BORDER_COLOR_DISABLED
  }
  return COLOR_INTENT.TOGGLE_BUTTON.BORDER_COLOR
}

export const ToggleButton: React.FC<React.PWC<ToggleButtonProps>> = ({
  value,
  isUnavailable,
  onChange,
  groupValue,
  name,
  height = TOGGLE_BUTTON_HEIGHT.PX_36,
  children,
}) => {
  const isChecked = value === groupValue
  const backgroundColor = getBackgroundColor(isUnavailable, isChecked)
  return (
    <ToggleButtonBase
      isUnavailable={isUnavailable}
      isChecked={isChecked}
      borderColor={getBorderdColor(isUnavailable, isChecked)}
      backgroundColor={backgroundColor}
      height={height}
      data-test-class={
        isUnavailable
          ? DATA_TEST_CLASS.TOGGLE_BUTTON_UNAVAILABLE
          : DATA_TEST_CLASS.TOGGLE_BUTTON
      }
    >
      <input
        type="radio"
        name={name}
        checked={isChecked}
        value={value}
        onChange={(event) => onChange(event, isUnavailable)}
      />
      {children}
    </ToggleButtonBase>
  )
}
